import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/core/api/base.api.client'
import { connectorsApi } from '@/core/api/connectors.api'
import type {
  Catalogue,
  Run,
  RunBody,
  RunSummary,
  Workflow,
  WorkflowIn,
  WorkflowSummary,
} from './types'

export const workflowsApi = {
  catalogue: () => api.get<Catalogue>('/workflows/catalogue'),
  status: () => api.get<{ backend: 'mongodb' | 'none'; configured: boolean; reachable: boolean; database: string | null; error: string | null }>('/workflows/status'),
  list: () => api.get<{ workflows: WorkflowSummary[] }>('/workflows'),
  get: (id: string) => api.get<{ workflow: Workflow }>(`/workflows/${encodeURIComponent(id)}`),
  create: (body: WorkflowIn) => api.post<{ workflow: Workflow }>('/workflows', body),
  update: (id: string, body: Partial<WorkflowIn>) =>
    api.patch<{ workflow: Workflow }>(`/workflows/${encodeURIComponent(id)}`, body),
  remove: (id: string) => api.delete<{ deleted: string }>(`/workflows/${encodeURIComponent(id)}`),
  duplicate: (id: string) =>
    api.post<{ workflow: Workflow }>(`/workflows/${encodeURIComponent(id)}/duplicate`),
  run: (id: string, body: RunBody) =>
    api.post<{ run: Run }>(`/workflows/${encodeURIComponent(id)}/run`, body),
  runs: (id: string, limit = 50) =>
    api.get<{ runs: RunSummary[] }>(`/workflows/${encodeURIComponent(id)}/runs`, { limit }),
  recentRuns: (limit = 50) => api.get<{ runs: RunSummary[] }>('/workflows/runs', { limit }),
  runDetail: (runId: string) => api.get<{ run: Run }>(`/workflows/runs/${encodeURIComponent(runId)}`),
  testExpression: (expression: string, context: Record<string, unknown>) =>
    api.post<{ ok: boolean; value?: unknown; type?: string; error?: string }>(
      '/workflows/expressions/test',
      { expression, context },
    ),
}

export const wfKeys = {
  catalogue: ['workflows', 'catalogue'] as const,
  status: ['workflows', 'status'] as const,
  list: ['workflows', 'list'] as const,
  one: (id: string) => ['workflows', 'one', id] as const,
  runs: (id: string) => ['workflows', 'runs', id] as const,
  recentRuns: ['workflows', 'recent-runs'] as const,
  run: (runId: string) => ['workflows', 'run', runId] as const,
}

export function useCatalogue() {
  return useQuery({ queryKey: wfKeys.catalogue, queryFn: workflowsApi.catalogue, staleTime: 5 * 60_000 })
}

export function useWorkflowStatus() {
  return useQuery({ queryKey: wfKeys.status, queryFn: workflowsApi.status, staleTime: 5 * 60_000 })
}

export function useWorkflows() {
  return useQuery({
    queryKey: wfKeys.list,
    queryFn: workflowsApi.list,
    // Active workflows run on their own; keep the list's last-run column honest.
    refetchInterval: (q) => (q.state.data?.workflows.some((w) => w.active) ? 20_000 : false),
  })
}

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: wfKeys.one(id ?? ''),
    queryFn: () => workflowsApi.get(id!),
    enabled: Boolean(id),
  })
}

export function useWorkflowRuns(id: string | undefined, live = false) {
  return useQuery({
    queryKey: wfKeys.runs(id ?? ''),
    queryFn: () => workflowsApi.runs(id!),
    enabled: Boolean(id),
    refetchInterval: live ? 10_000 : false,
  })
}

export function useRecentRuns() {
  return useQuery({ queryKey: wfKeys.recentRuns, queryFn: () => workflowsApi.recentRuns(), refetchInterval: 20_000 })
}

export function useRun(runId: string | undefined) {
  return useQuery({
    queryKey: wfKeys.run(runId ?? ''),
    queryFn: () => workflowsApi.runDetail(runId!),
    enabled: Boolean(runId),
  })
}

export function useWorkflowMutations() {
  const qc = useQueryClient()
  const invalidate = (id?: string) => {
    void qc.invalidateQueries({ queryKey: wfKeys.list })
    if (id) {
      void qc.invalidateQueries({ queryKey: wfKeys.one(id) })
      void qc.invalidateQueries({ queryKey: wfKeys.runs(id) })
    }
    void qc.invalidateQueries({ queryKey: wfKeys.recentRuns })
  }
  return {
    create: useMutation({ mutationFn: workflowsApi.create, onSuccess: () => invalidate() }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Partial<WorkflowIn> }) => workflowsApi.update(id, body),
      onSuccess: (_d, v) => invalidate(v.id),
    }),
    remove: useMutation({ mutationFn: workflowsApi.remove, onSuccess: () => invalidate() }),
    duplicate: useMutation({ mutationFn: workflowsApi.duplicate, onSuccess: () => invalidate() }),
    run: useMutation({
      mutationFn: ({ id, body }: { id: string; body: RunBody }) => workflowsApi.run(id, body),
      onSuccess: (_d, v) => invalidate(v.id),
    }),
  }
}

/** The action.json behind a connector node: its inputs, lookups and output shape. */
export function useActionSchema(connectorId: string | undefined, actionId: string | undefined) {
  return useQuery({
    queryKey: ['workflows', 'action-schema', connectorId ?? '', actionId ?? ''],
    queryFn: () => connectorsApi.actionSchema(connectorId!, actionId!),
    enabled: Boolean(connectorId && actionId),
    staleTime: 10 * 60_000,
  })
}
